import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Link } from "wouter";
import { Plus, Pencil, Trash2, ArrowLeft, Sparkles, Eye, Copy } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import type { UserAgentSkill } from "@/types/workflowEngine";
import { userAgentSkillsService } from "@/services/userAgentSkillsService";
import {
  BUILTIN_AGENT_SKILLS,
  builtinToFormValues,
  type AgentSkillDefinition,
} from "@/data/agentSkills";
import { SkillBuilderModal } from "@/components/agents/SkillBuilderModal";
import type { SkillFormValues } from "@/components/agents/SkillBuildForm";

function SkillScopes({ scopes }: { scopes?: string[] }) {
  if (!scopes?.length) {
    return <span className="text-xs text-muted-foreground">any</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {scopes.map((s) => (
        <Badge key={s} variant="secondary" className="text-xs">
          {s}
        </Badge>
      ))}
    </div>
  );
}

export default function AgentSkillsPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSkill, setEditingSkill] = useState<UserAgentSkill | null>(null);
  const [duplicateInitialValues, setDuplicateInitialValues] = useState<Partial<SkillFormValues> | null>(
    null
  );
  const [deleteTarget, setDeleteTarget] = useState<UserAgentSkill | null>(null);
  const [viewBuiltin, setViewBuiltin] = useState<AgentSkillDefinition | null>(null);

  const {
    data: skills = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["user-agent-skills", user?.id],
    queryFn: () => userAgentSkillsService.list(),
    enabled: !!user,
  });

  const saveMutation = useMutation({
    mutationFn: async (values: SkillFormValues) => {
      const payload = {
        name: values.name.trim(),
        description: values.description.trim() || null,
        instructions: values.instructions.trim(),
        integrationScopes: values.integrationScopes,
        outputSchema: values.outputSchema ?? null,
        isTemplate: false,
      };
      if (editingSkill) {
        return userAgentSkillsService.update(editingSkill.id, payload);
      }
      return userAgentSkillsService.create(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-agent-skills"] });
      setEditingSkill(null);
      setDuplicateInitialValues(null);
      toast({ title: editingSkill ? "Skill updated" : "Skill created" });
    },
    onError: (e: Error) => {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => userAgentSkillsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-agent-skills"] });
      setDeleteTarget(null);
      toast({ title: "Skill deleted" });
    },
    onError: (e: Error) => {
      toast({ title: "Failed to delete", description: e.message, variant: "destructive" });
    },
  });

  const openCreate = () => {
    setEditingSkill(null);
    setDuplicateInitialValues(null);
    setDialogOpen(true);
  };

  const openEdit = (skill: UserAgentSkill) => {
    setEditingSkill(skill);
    setDuplicateInitialValues(null);
    setDialogOpen(true);
  };

  const openDuplicate = (skill: AgentSkillDefinition) => {
    setEditingSkill(null);
    setDuplicateInitialValues(builtinToFormValues(skill));
    setDialogOpen(true);
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <Link href="/ai-agents">
            <Button variant="ghost" size="sm" className="mb-2 -ml-2">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Agents
            </Button>
          </Link>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Sparkles className="h-8 w-8 text-primary" />
            Agent Skills
          </h1>
          <p className="text-muted-foreground mt-1">
            Reusable instruction packs you can attach to any agent
          </p>
        </div>
        <Button onClick={openCreate} data-testid="button-create-skill">
          <Plus className="h-4 w-4 mr-2" />
          Create Skill
        </Button>
      </div>

      {isError && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
          <p className="text-sm text-destructive font-medium">Could not load your skills</p>
          <p className="text-sm text-destructive/90 mt-1">
            {error instanceof Error ? error.message : "Unknown error"}
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            If this persists, confirm migration 062 (user_agent_skills table) is applied in Supabase.
          </p>
        </div>
      )}

      <section className="space-y-3">
        <div>
          <h2 className="text-lg font-semibold">Built-in skills</h2>
          <p className="text-sm text-muted-foreground">
            Shipped with the app. View details or duplicate to customize your own copy.
          </p>
        </div>
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-3 text-sm font-medium">Name</th>
                <th className="text-left p-3 text-sm font-medium hidden md:table-cell">Scopes</th>
                <th className="text-left p-3 text-sm font-medium hidden lg:table-cell">Description</th>
                <th className="text-right p-3 text-sm font-medium w-32">Actions</th>
              </tr>
            </thead>
            <tbody>
              {BUILTIN_AGENT_SKILLS.map((skill) => (
                <tr key={skill.id} className="border-t">
                  <td className="p-3">
                    <div className="font-medium">{skill.name}</div>
                    <Badge variant="outline" className="text-xs mt-1">
                      Built-in
                    </Badge>
                  </td>
                  <td className="p-3 hidden md:table-cell">
                    <SkillScopes scopes={skill.integrationScopes} />
                  </td>
                  <td className="p-3 text-sm text-muted-foreground hidden lg:table-cell truncate max-w-xs">
                    {skill.description}
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => setViewBuiltin(skill)} title="View">
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openDuplicate(skill)}
                        title="Duplicate to customize"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="space-y-3">
        <div>
          <h2 className="text-lg font-semibold">Your skills</h2>
          <p className="text-sm text-muted-foreground">
            Skills you created or duplicated — edit and attach them to agents.
          </p>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : skills.length === 0 ? (
          <div className="border rounded-lg p-8 text-center">
            <p className="text-muted-foreground mb-4">
              No saved skills yet. Duplicate a built-in skill above or create your own.
            </p>
            <Button onClick={openCreate}>Create skill</Button>
          </div>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-3 text-sm font-medium">Name</th>
                  <th className="text-left p-3 text-sm font-medium hidden md:table-cell">Scopes</th>
                  <th className="text-left p-3 text-sm font-medium hidden lg:table-cell">Description</th>
                  <th className="text-left p-3 text-sm font-medium hidden xl:table-cell">Updated</th>
                  <th className="text-right p-3 text-sm font-medium w-28">Actions</th>
                </tr>
              </thead>
              <tbody>
                {skills.map((skill) => (
                  <tr key={skill.id} className="border-t">
                    <td className="p-3 font-medium">{skill.name}</td>
                    <td className="p-3 hidden md:table-cell">
                      <SkillScopes scopes={skill.integrationScopes} />
                    </td>
                    <td className="p-3 text-sm text-muted-foreground hidden lg:table-cell truncate max-w-xs">
                      {skill.description || "—"}
                    </td>
                    <td className="p-3 text-sm text-muted-foreground hidden xl:table-cell">
                      {skill.updatedAt
                        ? formatDistanceToNow(new Date(skill.updatedAt), { addSuffix: true })
                        : "—"}
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(skill)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(skill)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <SkillBuilderModal
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) {
            setEditingSkill(null);
            setDuplicateInitialValues(null);
          }
        }}
        skill={editingSkill}
        initialValues={duplicateInitialValues ?? undefined}
        onSave={(values) => saveMutation.mutateAsync(values)}
        isSaving={saveMutation.isPending}
      />

      <Dialog open={!!viewBuiltin} onOpenChange={(open) => !open && setViewBuiltin(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{viewBuiltin?.name}</DialogTitle>
          </DialogHeader>
          {viewBuiltin && (
            <div className="space-y-4 text-sm">
              <p className="text-muted-foreground">{viewBuiltin.description}</p>
              <div>
                <p className="font-medium mb-1">Scopes</p>
                <SkillScopes scopes={viewBuiltin.integrationScopes} />
              </div>
              <div>
                <p className="font-medium mb-1">Instructions</p>
                <pre className="whitespace-pre-wrap rounded-md bg-muted p-3 text-xs font-mono">
                  {viewBuiltin.instructions}
                </pre>
              </div>
              {viewBuiltin.outputSchema && (
                <div>
                  <p className="font-medium mb-1">Output schema</p>
                  <pre className="whitespace-pre-wrap rounded-md bg-muted p-3 text-xs font-mono overflow-x-auto">
                    {JSON.stringify(viewBuiltin.outputSchema, null, 2)}
                  </pre>
                </div>
              )}
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  openDuplicate(viewBuiltin);
                  setViewBuiltin(null);
                }}
              >
                <Copy className="h-4 w-4 mr-2" />
                Duplicate to customize
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete skill?</AlertDialogTitle>
            <AlertDialogDescription>
              &quot;{deleteTarget?.name}&quot; will be removed. Agents that attached it will keep the ID until you detach it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
